import { useCallback, useEffect, useState } from 'react'

export type Theme = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'freehold-theme'

export function getStoredTheme(): Theme {
  if (typeof localStorage === 'undefined') return 'system'
  const t = localStorage.getItem(STORAGE_KEY)
  return t === 'light' || t === 'dark' || t === 'system' ? t : 'system'
}

export function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return theme
}

export function applyTheme(theme: Theme): void {
  const resolved = resolveTheme(theme)
  document.documentElement.classList.toggle('dark', resolved === 'dark')
  document.documentElement.style.colorScheme = resolved
}

export function setTheme(theme: Theme): void {
  if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, theme)
  applyTheme(theme)
}

// initTheme is called once before React renders (in main.tsx) to avoid a
// flash of the wrong theme, and keeps "system" mode in sync with the OS.
export function initTheme(): void {
  applyTheme(getStoredTheme())
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => {
      if (getStoredTheme() === 'system') applyTheme('system')
    })
}

// useTheme drives theme-toggle UI. resolved is the concrete light/dark currently
// shown; cycle() rotates light -> dark -> system.
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme)
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolveTheme(getStoredTheme()))

  useEffect(() => {
    setResolved(resolveTheme(theme))
  }, [theme])

  const update = useCallback((t: Theme) => {
    setTheme(t)
    setThemeState(t)
    setResolved(resolveTheme(t))
  }, [])

  const cycle = useCallback(() => {
    const order: Theme[] = ['light', 'dark', 'system']
    const next = order[(order.indexOf(getStoredTheme()) + 1) % order.length]
    update(next)
  }, [update])

  return { theme, resolved, setTheme: update, cycle }
}
